/**
 * How each programme a lead owns is progressing through module review.
 *
 * The counts were coloured `text-info`, `text-warning` and `text-success` —
 * raw state colours as text on the canvas, which is the contrast defect the
 * `-strong` steps exist for, and colour alone carrying the meaning besides.
 *
 * @module components/overview/ModuleReviewTable
 */

import React from "react";

import { QueueStatusBadge } from "@/components/shared/QueueStatusBadge";
import {
  CollectionView,
  type Column,
  EmptyState,
  EntityCard,
  MutedText,
  Panel,
  useTableSort,
} from "@/components/ui";

export interface ModuleReviewRow {
  programmeId: string;
  title: string;
  moduleCount: number;
  draft: number;
  submitted: number;
  changes: number;
  approved: number;
  unassigned: number;
}

/**
 * The review states a module can be in, in the order a module moves through
 * them. `Unassigned` is not among them: it counts modules with no builder
 * whatever their review state, so every one is already inside another count
 * and chipping it beside them added up past the programme's module total.
 */
const MODULE_REVIEW_STATES: readonly {
  label: string;
  of: (row: ModuleReviewRow) => number;
}[] = [
  { label: "Draft", of: (row) => row.draft },
  { label: "Submitted for Review", of: (row) => row.submitted },
  { label: "Changes Requested", of: (row) => row.changes },
  { label: "Approved", of: (row) => row.approved },
];

export interface ModuleReviewTableProps {
  rows: readonly ModuleReviewRow[];
  error?: Error | null;
  /** The counts are in flight: a skeleton stands in for the rows, not the empty state. */
  loading?: boolean;
  /** The reader decides every programme's reviews, so leading none says nothing. */
  administers?: boolean;
}

/** A count of zero is not worth reading as a number; a dash says "none". */
function count(value: number): React.ReactNode {
  return value === 0 ? <MutedText size="caption">—</MutedText> : value;
}

/**
 * @example
 * ```tsx
 * <ModuleReviewTable rows={reviewRowsFor(session, programmes, modules)} />
 * ```
 */
export function ModuleReviewTable({
  rows,
  error = null,
  loading = false,
  administers = false,
}: ModuleReviewTableProps): React.ReactElement {
  // Every column declares a comparator, and `DataTable` draws no sort
  // control without a change handler, so without this pair they described
  // an ordering the table could never apply.
  const sort = useTableSort({ key: "programme", direction: "asc" });

  const columns: readonly Column<ModuleReviewRow>[] = [
    {
      key: "programme",
      header: "Programme",
      sortValue: (row) => row.title,
      render: (row) => (
        <div className="min-w-0">
          <div className="font-semibold">{row.title}</div>
          <MutedText size="caption" as="div">
            {row.moduleCount} {row.moduleCount === 1 ? "module" : "modules"}
          </MutedText>
        </div>
      ),
    },
    {
      key: "draft",
      header: "Draft",
      sortValue: (row) => row.draft,
      render: (row) => count(row.draft),
      align: "end",
      priority: 2,
    },
    {
      key: "submitted",
      header: "Submitted",
      sortValue: (row) => row.submitted,
      render: (row) => count(row.submitted),
      align: "end",
    },
    {
      key: "changes",
      header: "Changes requested",
      sortValue: (row) => row.changes,
      render: (row) => count(row.changes),
      align: "end",
    },
    {
      key: "approved",
      header: "Approved",
      sortValue: (row) => row.approved,
      render: (row) => count(row.approved),
      align: "end",
      priority: 2,
    },
    {
      key: "unassigned",
      header: "Unassigned",
      sortValue: (row) => row.unassigned,
      render: (row) => count(row.unassigned),
      align: "end",
      priority: 3,
    },
  ];

  // No rows is the same shape whether the read never answered or the account
  // leads nothing, and only one of those is a statement about the account.
  const unanswered = Boolean(error);
  let emptyTitle = "No programmes are assigned to you as Programme Lead";
  if (unanswered) {
    emptyTitle = "The module review counts are not available yet";
  } else if (administers) {
    emptyTitle = "No programmes have module reviews to report";
  }
  const empty = <EmptyState icon="users-three" title={emptyTitle} density="row" />;

  return (
    <Panel
      title="Module review status"
      icon="chart-bar"
      collapsible
      data-testid="module-review-status-report"
    >
      <CollectionView
        loading={loading}
        loadingLabel="Loading module review status"
        storageKey="module-review"
        {...sort}
        caption="Module review status by programme"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.programmeId}
        rowTestId={(row) => `module-review-row-${row.programmeId}`}
        emptyState={empty}
        cards={{
          render: (row) => (
            <EntityCard
              title={row.title}
              meta={`${row.moduleCount} ${row.moduleCount === 1 ? "module" : "modules"}`}
              data-testid={`module-review-card-${row.programmeId}`}
            >
              {/* One chip per state the programme is actually in, counted, in
                  the review vocabulary the queue uses. Spelling all five as
                  label/value rows spent four of them on an em dash each;
                  dropping the empty ones instead left a lone "Draft 5" with
                  nothing to read it against, so the module count above is the
                  denominator and the chips are the breakdown. */}
              <div className="flex flex-wrap gap-1.5">
                {MODULE_REVIEW_STATES.filter((state) => state.of(row) > 0).map((state) => (
                  <QueueStatusBadge
                    key={state.label}
                    status={state.label}
                    kind="Module"
                    size="sm"
                    count={state.of(row)}
                  />
                ))}
              </div>
              {/* Its own line, not a chip: it cuts across the states above
                  rather than partitioning them alongside them. */}
              {row.unassigned > 0 && <div className="mt-2">{row.unassigned} with no builder</div>}
            </EntityCard>
          ),
        }}
      />
    </Panel>
  );
}
